'use client'

import { isWorkEmail } from './is-work-email'
import { zodResolver } from '@hookform/resolvers/zod'
import { Form as FormType } from '@telesoft/types/payload-types'
import { usePathname, useRouter } from 'next/navigation'
import React from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

export function useFormHook(props: FormType) {
  const [error, setError] = React.useState<{ status?: string; message: string } | undefined>()
  const [isLoading, setIsLoading] = React.useState(false)
  const [hasSubmitted, setHasSubmitted] = React.useState<boolean>()
  const router = useRouter()
  const pathname = usePathname()

  const cmsForm = props

  if (cmsForm?.fields === undefined || cmsForm?.fields === null) {
    throw new Error('Fields undefined.')
  }

  const schemaObject = cmsForm.fields.reduce(
    (acc, field) => {
      if ('name' in field) {
        if ('required' in field && field.required) {
          return { ...acc, [field.name]: z.string().min(1, { message: 'This field is required' }) }
        } else {
          // TODO: fix field required bug
          return { ...acc, [field.name]: z.string().nullable().optional() }
        }
      }
      return acc
    },
    {} as Record<string, any>,
  )

  const schema = z.object(schemaObject)

  type ExistingFormFillDataType = {
    firstname?: string | null
    lastname?: string | null
    email?: string | null
    company?: string | null
    companyname?: string | null
    phone?: string | null
  }

  const existingFormFillData: ExistingFormFillDataType | null =
    typeof window !== 'undefined' && window.localStorage.getItem('__formRefill')
      ? JSON.parse(window.localStorage.getItem('__formRefill') || 'null')
      : null

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      firstname: existingFormFillData?.firstname,
      lastname: existingFormFillData?.lastname,
      email: existingFormFillData?.email,
      company: existingFormFillData?.company || existingFormFillData?.companyname,
      phone: existingFormFillData?.phone,
    },
  })

  const onSubmit = React.useCallback(
    async ({ data }: { data: any }) => {
      window.localStorage.setItem(
        '__formRefill',
        JSON.stringify({
          firstname: data?.firstname,
          lastname: data?.lastname,
          email: data?.email,
          company: data?.company,
          companyname: data?.companyname,
          phone: data?.phone,
        }),
      )
      // TODO: enable conditionally based on privacy pref
      window.localStorage.setItem('__isFormRefillEnabled', 'true')

      let loadingTimerID: NodeJS.Timeout | undefined

      const emailField = data?.email as string | null
      const attachmentField = data?.attachment as File | null

      if (emailField) {
        const isWorkE = isWorkEmail(emailField)
        if (!isWorkE) {
          form.setError('email', { message: 'Please use a work email' })
          return false
        }
      }

      let attachmentId: string | undefined
      if (attachmentField) {
        const isPdf = attachmentField?.type === 'application/pdf'
        if (!isPdf) {
          form.setError('attachment', { message: 'Please upload a PDF' })
          return false
        }
        const formData = new FormData()
        formData.append('file', attachmentField)
        const result = await fetch(`/api/upload`, {
          method: 'POST',
          body: formData,
        })
        const json = await result.json()
        if (!json.success) {
          form.setError('attachment', { message: 'Something went wrong' })
          return false
        }
        const fileId = json.file.id
        attachmentId = json.file.id
      }

      const submitForm = async () => {
        setError(undefined)

        const dataToSend = Object.entries(data)
          .filter(([name]) => name !== 'attachment')
          .map(([name, value]) => ({
            field: name,
            value,
          }))

        loadingTimerID = setTimeout(() => {
          setIsLoading(true)
        }, 1000)

        try {
          const pageUri = `${process.env.NEXT_PUBLIC_HOST_URL}${pathname}`
          const slugParts = pathname?.split('/')
          const pageName = slugParts?.at(-1) === '' ? 'Home' : slugParts?.at(-1)

          const req = await fetch(
            `${process.env.NEXT_PUBLIC_SERVER_BASE_URL}/api/form-submissions`,
            {
              method: 'POST',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                form: cmsForm.id,
                submissionData: dataToSend,
                attachment: attachmentId,
                pageUri,
                pageName,
              }),
            },
          )

          const res = await req.json()
          const formSubmissionCreatedDocId = res.doc.id

          clearTimeout(loadingTimerID)

          if (req.status >= 400) {
            setIsLoading(false)
            setError({
              status: res.status,
              message: res.errors?.[0]?.message || 'Internal Server Error',
            })

            return
          }

          // !fix
          // if (attachmentField) {
          //   try {
          //     const req = await fetch(`/api/upload/link?doc=${formSubmissionCreatedDocId}`, {
          //       method: 'GET',
          //       headers: {
          //         'Content-Type': 'application/json',
          //       },
          //       // body: JSON.stringify({
          //       //   docId: formSubmissionCreatedDocId,
          //       // }),
          //     })
          //     const data = await req.json()
          //     console.log('FORM CLIENT SUBMISSION RESPONSE: ', data)
          //   } catch (err) {
          //     console.log(err)
          //   }
          // }

          setIsLoading(false)
          setHasSubmitted(true)

          if (cmsForm.confirmationType === 'redirect' && cmsForm.redirect) {
            const { url } = cmsForm.redirect
            if (!url) return

            const redirectUrl = new URL(url, process.env.NEXT_PUBLIC_HOST_URL)

            try {
              if (url.startsWith('/') || redirectUrl.origin === process.env.NEXT_PUBLIC_HOST_URL) {
                router.push(redirectUrl.href)
              } else {
                window.location.assign(url)
              }
            } catch (err) {
              console.warn(err)
              setError({
                message: 'Something went wrong. Did not redirect.',
              })
            }
          }
        } catch (err) {
          console.warn(err)
          setIsLoading(false)
          setError({
            message: 'Something went wrong.',
          })
        }
      }

      submitForm()
    },
    [router, cmsForm.id, cmsForm.redirect, cmsForm.confirmationType, pathname],
  )

  return {
    form,
    error,
    isLoading,
    hasSubmitted,
    onSubmit,
  }
}
